import React, { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from './theme';

// Define types for authentication context
interface UserContextType {
  email: string;
  setUserTheme: (theme: string) => void;
  setUserLanguage: (language: string) => void;
}

// Create authentication context
export const UserContext = createContext<UserContextType>(
  {} as UserContextType
);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation(); // Use useTranslation hook to access i18n instance
  const { toggleTheme }: any = useTheme()
  const [email, setEmail] = useState('');

  useEffect(() => {
    initUserTheme();
    initUserLanguage();
  }, [])

  const initUserLanguage = () => {
    const storedLanguage = localStorage.getItem('language');
    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage)
    } else {
      const browserLanguage = window.navigator.language
      i18n.changeLanguage(browserLanguage)
    }
  }

  const initUserTheme = () => {
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme) {
      toggleTheme(storedTheme)
    } else {
      const darkTheme = window.matchMedia("(prefers-color-scheme: dark)");
      if (darkTheme.matches) {
        toggleTheme('dark')
      }
    }
  }

  const setUserTheme = (theme: string) => {
    toggleTheme(theme)
    localStorage.setItem('theme', theme)
  }

  const setUserLanguage = (language: string) => {
    i18n.changeLanguage(language)
    localStorage.setItem('language', language)
  }

  return (
    <UserContext.Provider
      value={{ email, setUserTheme, setUserLanguage }}
    >
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to access authentication context
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useAuth must be used within an UserProvider');
  }
  return context;
};
